import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { TAnyClientQuestionType, TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import { mergeRulesById, removeRulesByIds } from './ruleMerge';
import type { EEvaluationType } from 'types/evaluation.types';
import type { TQuestionRule } from 'types/rules.types';

// NOTE:
// Prefix "c" stands for "current"
type LanguageState = {
  c: TAvailableLanguageCodes;
  list: TAvailableLanguageCodes[];
};

export type QuestionBelongToAnotherClient = {
  message: string;
  swapOptions: { questions: TAnyClientQuestionType[] };
  questionId: string;
};

type LibraryEntry = TAnyClientQuestionType & {
  language: LanguageState;
  questionBelongToAnotherClient?: QuestionBelongToAnotherClient;
};

type LibraryState = Record<string, LibraryEntry>;

const initialState: LibraryState = {};

const LibrarySlice = createSlice({
  name: 'Library',
  initialState,
  reducers: {
    addToLibrary: (
      state,
      action: PayloadAction<{
        question: TAnyClientQuestionType;
        cLanguage?: TAvailableLanguageCodes;
      }>,
    ) => {
      const { question, cLanguage } = action.payload;
      state[question.id] = {
        ...question,
        language: {
          c: cLanguage ?? question.available_languages[0],
          list: question.available_languages,
        },
      };
    },

    addManyToLibrary: (
      state,
      action: PayloadAction<{
        questions: TAnyClientQuestionType[];
        cLanguage?: TAvailableLanguageCodes;
        questionBelongToAnotherClient?: QuestionBelongToAnotherClient[];
      }>,
    ) => {
      const { questions, cLanguage, questionBelongToAnotherClient } = action.payload;

      for (const q of questions) {
        const found = questionBelongToAnotherClient?.find((m) => m.questionId === q.id);

        state[q.id] = {
          ...q,
          language: {
            c: cLanguage ?? q.available_languages[0],
            list: q.available_languages,
          },
          ...(found ? { questionBelongToAnotherClient: found } : {}),
        };
      }
    },
    updateSwapOptionsToLibrary: (
      state,
      action: PayloadAction<{
        questionBelongToAnotherClient?: QuestionBelongToAnotherClient[];
      }>,
    ) => {
      const { questionBelongToAnotherClient } = action.payload;
      for (const swap of questionBelongToAnotherClient ?? []) {
        const current = state[swap.questionId] ?? {};

        state[swap.questionId] = {
          ...current,
          questionBelongToAnotherClient: swap,
        };
      }
    },

    // getQuestionById only ever populates a Library entry once (its onQueryStarted guards on
    // `!(questionId in library)`), so nothing ever refetches it afterward — a direct-to-question
    // write elsewhere (apply-evaluation-change) needs to patch the cached copy itself, or every
    // fallback-to-question-default read (evaluateSingleSelect/evaluateMultipleSelect) keeps seeing
    // the value from before the write. Harmless when a template override exists for this option —
    // that always wins over this value regardless, so it just sits masked and unused.
    updateLibraryQuestionOptionEvaluation: (
      state,
      action: PayloadAction<{
        questionId: string;
        optionId: string;
        evaluation: EEvaluationType;
        bonus_points?: number;
      }>,
    ) => {
      const { questionId, optionId, evaluation, bonus_points } = action.payload;
      const entry = state[questionId] as unknown as
        | { options?: { id: string; evaluation: EEvaluationType; bonus_points?: number }[] }
        | undefined;
      const option = entry?.options?.find((o) => o.id === optionId);
      if (!option) return;
      option.evaluation = evaluation;
      option.bonus_points = bonus_points;
    },

    // Same staleness problem as updateLibraryQuestionOptionEvaluation, but for rules —
    // apply-rule-change's response also only carries the template, never the question. The
    // frontend only ever computes the upsert/remove delta (not the resulting array), so the merge
    // must mirror the backend's mergeRulesById/removeRulesByIds exactly (see ./ruleMerge), or the
    // cached copy would drift from what's actually on the question.
    updateLibraryQuestionRules: (
      state,
      action: PayloadAction<{
        questionId: string;
        upsertRules?: TQuestionRule[];
        removeRuleIds?: string[];
      }>,
    ) => {
      const { questionId, upsertRules, removeRuleIds } = action.payload;
      const entry = state[questionId] as unknown as { rules?: TQuestionRule[] } | undefined;
      if (!entry) return;
      let nextRules = entry.rules ?? [];
      if (upsertRules?.length) {
        nextRules = mergeRulesById(nextRules, upsertRules);
      }
      if (removeRuleIds?.length) {
        nextRules = removeRulesByIds(nextRules, removeRuleIds);
      }
      (entry as { rules: TQuestionRule[] }).rules = nextRules;
    },

    setCurrentQuestionLanguage: (
      state,
      action: PayloadAction<{
        questionId: string;
        language: TAvailableLanguageCodes;
      }>,
    ) => {
      const { questionId, language } = action.payload;

      const entry = state[questionId];
      if (!entry) return; // guard if not present

      entry.language = {
        ...entry.language,
        c: language,
      };
    },
  },
});

export const {
  addToLibrary,
  addManyToLibrary,
  setCurrentQuestionLanguage,
  updateLibraryQuestionOptionEvaluation,
  updateLibraryQuestionRules,
  updateSwapOptionsToLibrary,
} = LibrarySlice.actions;
export default LibrarySlice.reducer;
